import { useParams, Link } from 'react-router-dom';
import SEOMeta from '@/components/base/SEOMeta';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { allComics } from '@/mocks/comics';

export default function ComicDetail() {
  const { id } = useParams<{ id: string }>();
  const comic = allComics.find((c) => c.id === id);

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-300">
              <i className="ri-emotion-sad-line text-3xl"></i>
            </div>
            <h1 className="text-xl font-heading text-foreground-900 mb-2">漫画未找到</h1>
            <p className="text-foreground-500 text-sm mb-6">抱歉，您查找的漫画不存在或已下架</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors duration-200"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedComics = allComics
    .filter((c) => c.category === comic.category && c.id !== comic.id)
    .slice(0, 4);

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 香香漫画免费在线阅读`,
    description: comic.description,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    datePublished: comic.chapters[0]?.updatedAt || comic.updatedAt,
    dateModified: comic.updatedAt,
    publisher: {
      '@type': 'Organization',
      name: '香香漫画',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.xiangxiangcomic.com/' },
      { '@type': 'ListItem', position: 2, name: comic.category, item: `https://www.xiangxiangcomic.com/category?type=${comic.category}` },
      { '@type': 'ListItem', position: 3, name: comic.title },
    ],
  };

  return (
    <>
      <SEOMeta
        title={`${comic.title} - 香香漫画免费在线阅读 | ${comic.author}`}
        description={`${comic.title}，${comic.author}作品。${comic.description.slice(0, 100)}... 香香漫画网提供${comic.category}免费在线阅读，下拉式追更体验。`}
        keywords={`${comic.title},${comic.category},香香漫画,漫画免费阅读,${comic.author},在线漫画`}
        canonicalUrl={`https://www.xiangxiangcomic.com/comic/${comic.id}`}
        ogType="article"
        ogImage={comic.cover}
        schema={{ ...schema, ...breadcrumbSchema }}
      />

      <Navbar />

      <main className="pt-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: comic.category, href: `/category?type=${comic.category}` },
              { label: comic.title },
            ]}
          />

          <article className="mb-12">
            <div className="flex flex-col lg:flex-row gap-8 md:gap-10">
              <div className="flex-shrink-0 w-full sm:w-64 lg:w-72 mx-auto lg:mx-0">
                <div className="relative overflow-hidden rounded-2xl bg-background-100 aspect-[3/4]">
                  <img
                    src={comic.cover}
                    alt={`${comic.title} - ${comic.category}漫画免费阅读`}
                    title={`${comic.title} - 香香漫画${comic.category}在线免费阅读`}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <h1 className="text-2xl md:text-3xl font-heading text-foreground-950 mb-3">
                  {comic.title}
                </h1>

                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
                    {comic.category}
                  </span>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    comic.status === '已完结'
                      ? 'bg-accent-100 text-accent-700'
                      : 'bg-secondary-100 text-secondary-700'
                  }`}>
                    {comic.status}
                  </span>
                  {comic.tags.map((tag) => (
                    <span key={tag} className="px-3 py-1 rounded-full bg-background-200 text-foreground-500 text-xs">
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="flex flex-wrap items-center gap-4 md:gap-6 text-sm text-foreground-500 mb-5">
                  <div className="flex items-center gap-1.5">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-user-line"></i>
                    </div>
                    <strong className="text-foreground-700 font-medium">作者：</strong>
                    <span>{comic.author}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-star-fill text-accent-500"></i>
                    </div>
                    <strong className="text-foreground-700 font-medium">评分：</strong>
                    <span className="text-accent-600 font-semibold">{comic.rating}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-eye-line"></i>
                    </div>
                    <strong className="text-foreground-700 font-medium">阅读：</strong>
                    <span>{comic.views >= 10000 ? `${(comic.views / 10000).toFixed(0)}万` : comic.views}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-time-line"></i>
                    </div>
                    <strong className="text-foreground-700 font-medium">更新：</strong>
                    <span>{comic.updatedAt}</span>
                  </div>
                </div>

                <div className="bg-background-100 rounded-xl p-5 mb-6">
                  <h2 className="text-sm font-medium text-foreground-700 mb-2">作品简介</h2>
                  <p className="text-sm text-foreground-500 leading-relaxed">{comic.description}</p>
                </div>

                <div className="flex flex-wrap gap-3">
                  <button className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer">
                    开始阅读（第1话）
                  </button>
                  <button className="px-6 py-2.5 bg-background-100 text-foreground-700 rounded-full text-sm font-medium hover:bg-background-200 transition-all duration-200 whitespace-nowrap cursor-pointer">
                    收藏漫画
                  </button>
                </div>
              </div>
            </div>
          </article>

          <section className="mb-12">
            <h2 className="text-xl font-heading text-foreground-950 mb-5 flex items-center gap-2">
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-list-check text-primary-500"></i>
              </div>
              章节列表
              <span className="text-sm font-normal text-foreground-400 ml-2">
                共 {comic.chapters.length} 话
              </span>
            </h2>

            <div className="bg-background-100 rounded-2xl p-4 md:p-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {comic.chapters.map((chapter) => (
                  <button
                    key={chapter.id}
                    className="flex items-center justify-between px-4 py-3 rounded-xl hover:bg-background-200 transition-colors duration-200 cursor-pointer text-left group"
                  >
                    <span className="text-sm text-foreground-700 group-hover:text-primary-500 transition-colors duration-200">
                      {chapter.title}
                    </span>
                    <span className="text-xs text-foreground-400 flex-shrink-0 ml-2">{chapter.updatedAt}</span>
                  </button>
                ))}
              </div>
            </div>
          </section>

          {relatedComics.length > 0 && (
            <section className="mb-12">
              <h2 className="text-xl font-heading text-foreground-950 mb-5">
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {relatedComics.map((c) => (
                  <Link
                    key={c.id}
                    to={`/comic/${c.id}`}
                    className="group cursor-pointer"
                  >
                    <div className="relative overflow-hidden rounded-xl bg-background-100 mb-3 aspect-[3/4]">
                      <img
                        src={c.cover}
                        alt={`${c.title} - 相关推荐`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                    <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors duration-200 line-clamp-1">
                      {c.title}
                    </h3>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}